/**
 * Search tips (docs/concepts/concept_advanced_search_syntax.md §5.4): a help
 * button in the search bar with a popover explaining the syntax.
 */

import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { onSearchTipsRequest, requestSearchTips } from './searchBarEvents';

const EXAMPLES = [
  { query: 'alarm list', key: 'anyWord' },
  { query: '"alarm list"', key: 'phrase' },
  { query: 'alarm AND trend', key: 'allWords' },
  { query: 'alarm OR event', key: 'eitherWord' },
  { query: 'alarm NOT web', key: 'leaveOut' },
] as const;

interface SearchTipsButtonProps {
  /** Called when the tips open, e.g. to close the suggestions dropdown. */
  onOpen?: () => void;
}

export function SearchTipsButton({ onOpen }: SearchTipsButtonProps) {
  const { t } = useTranslation('search');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const titleId = 'search-tips-title';

  // "See search tips" in the landing page tip line.
  useEffect(
    () =>
      onSearchTipsRequest(() => {
        onOpen?.();
        setAnchor(buttonRef.current);
      }),
    [onOpen]
  );

  return (
    <>
      <IconButton
        ref={buttonRef}
        size="small"
        aria-label={t('syntax.tips.button')}
        aria-haspopup="dialog"
        aria-expanded={anchor !== null}
        onClick={(event) => {
          onOpen?.();
          setAnchor(event.currentTarget);
        }}
        sx={{ mr: 1, color: 'text.secondary' }}
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { role: 'dialog', 'aria-labelledby': titleId, sx: { p: 2, maxWidth: 420 } },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography id={titleId} variant="subtitle1" component="h2" sx={{ flex: 1, fontWeight: 600 }}>
            {t('syntax.tips.title')}
          </Typography>
          <IconButton size="small" aria-label={t('syntax.tips.close')} onClick={() => setAnchor(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {EXAMPLES.map(({ query, key }) => (
            <Box component="li" key={key} sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, py: 0.75 }}>
              <Box
                component="code"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  bgcolor: 'surfaceContainerHigh',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {query}
              </Box>
              <Typography variant="body2">{t(`syntax.tips.${key}`)}</Typography>
            </Box>
          ))}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('syntax.tips.footer')}
        </Typography>
      </Popover>
    </>
  );
}

const CODE_SX = {
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  bgcolor: 'surfaceContainerHigh',
  color: 'text.primary',
  px: 0.5,
  py: 0.125,
  borderRadius: 0.5,
} as const;

/** One-line hint below the landing page search bar; the link opens the search tips. */
export function SearchTipLine() {
  const { t } = useTranslation('search');
  return (
    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1.5 }}>
      <Trans t={t} i18nKey="syntax.tips.line" components={{ code: <Box component="code" sx={CODE_SX} /> }} />{' '}
      <Link component="button" type="button" onClick={requestSearchTips} sx={{ verticalAlign: 'baseline' }}>
        {t('syntax.tips.lineLink')}
      </Link>
    </Typography>
  );
}
